import { x } from "@xstyled/styled-components";

import * as Banner from "@hire/components/Banner";
import { Text } from "@otta/design";
import { locationToLocationOption } from "@hire/util/locations";
import { Location } from "@hire/schema";

const getTitle = ({
  hasOnlyNonMatchingLocations,
  shouldMentionSourcing,
}: {
  hasOnlyNonMatchingLocations: boolean;
  shouldMentionSourcing: boolean;
}) => {
  if (hasOnlyNonMatchingLocations) {
    return shouldMentionSourcing
      ? "This job's location doesn't support matching and sourcing"
      : "This job's location doesn't support matching";
  }
  return shouldMentionSourcing
    ? "Not all locations support matching and sourcing"
    : "Not all locations support matching";
};

const getDescription = ({
  hasOnlyNonMatchingLocations,
  shouldMentionSourcing,
  supportedLocationsFormatted,
}: {
  hasOnlyNonMatchingLocations: boolean;
  shouldMentionSourcing: boolean;
  supportedLocationsFormatted: string;
}) => {
  if (hasOnlyNonMatchingLocations) {
    return shouldMentionSourcing
      ? "We're working on bringing matching to jobs and candidates in other locations. For now, this job is only shown on your company profile. Sourcing is not available for this job."
      : "We're working on bringing matching to jobs and candidates in other locations. For now, this job is only shown on your company profile.";
  }
  return shouldMentionSourcing
    ? `We're working on bringing matching and sourcing to more locations. For now, this job is shown on your company profiles, and for candidates looking in ${supportedLocationsFormatted}. In sourcing, you'll only see candidates in these locations.`
    : `We're working on bringing matching to jobs and candidates in other locations. For now, this job is shown on your company profile, and to candidates looking in ${supportedLocationsFormatted}.`;
};

export const LocationSupportMatchingInformation = ({
  pathname,
  supportedLocations,
  hasSourcing = false,
}: {
  pathname: string;
  supportedLocations: Location[];
  hasSourcing?: boolean;
}) => {
  const isSourcingPage = pathname.endsWith("/sourcing/search");
  const isAnalyticsPage = pathname.endsWith("analytics");
  const isEditPage = pathname.endsWith("/edit");

  if (!isSourcingPage && !isAnalyticsPage && !isEditPage) {
    return null;
  }

  const hasOnlyNonMatchingLocations = supportedLocations.length === 0;
  const shouldMentionSourcing = hasSourcing;

  const supportedLocationsFormatted = supportedLocations
    .map(location => locationToLocationOption(location)?.label)
    .join(", ");

  const title = getTitle({
    hasOnlyNonMatchingLocations,
    shouldMentionSourcing,
  });

  const description = getDescription({
    hasOnlyNonMatchingLocations,
    shouldMentionSourcing,
    supportedLocationsFormatted,
  });

  return (
    <x.div w="100%" mt={16}>
      <Banner.Info name="paused">
        <Banner.Content>
          <Text bold>{title}</Text>
          <Text size={0}>{description}</Text>
        </Banner.Content>
      </Banner.Info>
    </x.div>
  );
};
